<template>
  <ListTable
    v-if="!isMobile"
    :columns="columns"
    :data="rows"
    @sortItemSelected="sortItemSelected"
  />
  <div v-else>
    <div class="btns">
      <div class="input-box">
        <input
          class="checkbox"
          type="checkbox"
          id="hideSymbol"
          v-model="hideOtherSymbol"
        />
        <label for="hideSymbol"></label>
        <span>{{ $t("trading.table.hideSymbol") }}</span>
      </div>
      <button @click="sortItemSelected({ key: 'all' })">
        {{ $t("trading.table.cancelAll") }}
      </button>
    </div>
    <div class="gray-line"></div>
    <NodataView v-if="rows.length === 0" min-height="300px" />
    <div class="row-wrapper" v-for="row in rows" :key="row.ordid">
      <div class="symbol">
        <p>{{ row.symbol }}USDT</p>
        <button @click="cancelHandler(row.ordid)">
          {{ $t("trading.table.cancel") }}
        </button>
      </div>
      <div class="sub">
        <span :class="row.side === 'buy' ? 'txt-plus' : 'txt-minus'"
          >{{ typeFormatter(row.type) }} /
          {{
            row.side === "buy" ? $t(`trading.table.buy`) : $t(`trading.table.sell`)
          }}</span
        >
        <span>{{ row.time }}</span>
      </div>
      <div class="coin-info">
        <p>{{ $t("trading.table.price") }}</p>
        <span>{{ formatNumberWithComma(row.price) }}</span>
      </div>
      <div class="coin-info">
        <p>{{ $t("trading.table.amount") }}</p>
        <span>{{ formatNumberWithComma(row.amount) }}</span>
      </div>
      <div class="coin-info">
        <p>TP/SL</p>
        <span>{{ row.tpsl }}</span>
      </div>
      <div class="gray-line"></div>
    </div>
  </div>
  <ModalConfirm
    :isVisible="confirmDialog.isVisible"
    :content="confirmDialog.content"
    @closeDialogHandler="closeDialogHandler"
  />
  <ModalAlert
    :isVisible="alertDialog.isVisible"
    :content="alertDialog.content"
    @closeDialogHandler="alertDialog.isVisible = false"
    :is-btn="true"
  />
</template>

<script setup>
import ModalConfirm from "@/components/common/modal/ModalConfirm.vue";
import ModalAlert from "@/components/common/modal/ModalAlert.vue";
import ListTable from "@/components/common/list/List.vue";
import NodataView from "@/components/common/noData/NodataView.vue";
import {
  cancelFormatter,
  sideFormatter,
  symbolFormatter,
  typeFormatter,
} from "@/utils/formatter";
import { amountFormatter, formatNumberWithComma } from "@/utils/order";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
const { t, locale } = useI18n();
const store = useStore();
const confirmDialog = ref({
  isVisible: false,
  key: "",
  content: "",
});
const alertDialog = ref({
  isVisible: false,
  key: "",
  content: "",
});
const columns = ref([
  { field: "time", title: t("trading.table.time"), sortable: false },
  {
    field: "symbol",
    title: t("trading.table.symbol"),
    sortable: false,
    formatter: symbolFormatter,
  },
  {
    field: "type",
    title: t("trading.table.type"),
    sortable: false,
    formatter: typeFormatter,
  },
  {
    field: "side",
    title: t("trading.table.side"),
    sortable: false,
    formatter: sideFormatter,
  },
  { field: "price", title: t("trading.table.price"), sortable: false },
  { field: "amount", title: t("trading.table.amount"), sortable: false },
  { field: "tpsl", title: "TP/SL", sortable: false },
  {
    field: "cancel",
    title: t("trading.table.cancel"),
    sortable: true,
    sortItems: [
      { key: "all", value: t("trading.table.all") },
      { key: "limit", value: t("trading.table.limit") },
      { key: "stop-limit", value: t("trading.table.stoplimit") },
    ],
    formatter: cancelFormatter,
  },
]);


const sizeType = computed(() => store.state.transact.sizeType);
const userNo = computed(() => store.state.userInfo.userInfo.user_no);
const isMobile = computed(() => store.state.account.isMobile);
const reCode = computed(() => store.state.user_history.reCode);
const hideOtherSymbol = computed({
  get: () => store.state.user_history.hideOtherSymbol,
  set: (value) => store.commit("user_history/setHideOtherSymbol", value),
});
const openOrderList = computed(() => {
  const otherSymbol = store.state.user_history.hideOtherSymbol;
  const list = store.state.user_history.openOrderList;
  const symbol = store.state.transact.ticker;
  let filteredList;
  if (otherSymbol) {
    filteredList = list.filter((v) => v.symbol === symbol);
  } else {
    filteredList = list;
  }

  return filteredList;
});
const rows = computed(() => {
  return openOrderList.value.map((v) => {
    const tp = v.tp === "0.0" ? "--" : v.tp;
    const sl = v.sl === "0.0" ? "--" : v.sl;
    return {
      time: v.datetime,
      symbol: v.symbol,
      type: v.type,
      side: v.side,
      price: v.price,
      amount: amountFormatter(sizeType.value === "USDT" ? v.margin : v.amount, v.symbol),
      tpsl: `${formatNumberWithComma(tp)} / ${formatNumberWithComma(sl)}`,
      ordid: v.ordid,
    };
  });
});

window.dispatchCancel = (ordid) => {
  if (ordid) cancelHandler(ordid);
};

const cancelHandler = async (ordid) => {
  await store.dispatch("user_history/postCancelOrder", { userNo: userNo.value, ordid });
  if (reCode.value !== 0) {
    alertDialog.value.isVisible = true;
    alertDialog.value.content = t(`trading.error.${reCode.value}`);
  } else {
    await store.dispatch("account/fetchUserWallet");
    await store.dispatch("user_history/fetchAllHistory", userNo.value);
  }
};

const sortItemSelected = (item) => {
  confirmDialog.value.isVisible = true;
  confirmDialog.value.key = item.key;
  switch (item.key) {
    case "all":
      confirmDialog.value.content = t("trading.openOrders.cancel1");
      break;
    case "limit":
      confirmDialog.value.content = t("trading.openOrders.cancel2");
      break;
    case "stop-limit":
      confirmDialog.value.content = t("trading.openOrders.cancel3");
      break;
  }
};

const closeDialogHandler = async (payload) => {
  confirmDialog.value.isVisible = false;
  if (payload === 1) {
    await store.dispatch("user_history/postCancelOrderAll", {
      userNo: userNo.value,
      ordid: confirmDialog.value.key,
    });
    await store.dispatch("account/fetchUserWallet");
    await store.dispatch("user_history/fetchAllHistory", userNo.value);
    if (reCode.value !== 0) {
      alertDialog.value.isVisible = true;
      alertDialog.value.content = t(`trading.error.${reCode.value}`);
    }
  }
};
watch(locale,()=>{
  columns.value[0].title = t("trading.table.time");
  columns.value[1].title = t("trading.table.symbol");
  columns.value[2].title = t("trading.table.type");
  columns.value[3].title = t("trading.table.side");
  columns.value[4].title = t("trading.table.price");
  columns.value[5].title = t("trading.table.amount");
  columns.value[6].title = t("trading.table.cancel");
  columns.value[6].sortItems[0].value = t("trading.table.all");
  columns.value[6].sortItems[1].value = t("trading.table.limit");
  columns.value[6].sortItems[2].value = t("trading.table.stoplimit");
})
</script>
<style lang="scss" scoped>
@import "@/assets/scss/components/trading/history/openOrders.scss";
</style>
